import { useCallback, useEffect, useRef, useState } from 'react'
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js'
import { useAppSupabase } from './supabase'

export interface RealtimeBinding {
  table: string
  /** Defaults to 'public'. */
  schema?: string
  /** Defaults to '*'. */
  event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*'
  /** PostgREST-style filter, e.g. `user_id=eq.${userId}`. */
  filter?: string
}

export type RealtimeSyncStatus = 'idle' | 'connecting' | 'live' | 'degraded'

export interface RealtimeSyncOptions {
  /** Channel name, or null while the inputs are unknown — nothing subscribes. */
  channel: string | null
  bindings: RealtimeBinding[]
  onChange: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
  /**
   * Re-read from the API. Runs when the channel re-subscribes after a drop, when
   * the tab comes back to the foreground, and on the degraded poll. Defaults to
   * calling `onChange` with no payload.
   */
  onResync?: () => void
  /** Plain-refetch cadence while the socket is not live. 0 disables it. */
  degradedPollMs?: number
  onStatus?: (status: string, err?: Error) => void
}

const DEFAULT_DEGRADED_POLL_MS = 60_000
const MAX_BACKOFF_MS = 30_000
/**
 * visibilitychange, focus and online all fire when a phone comes back from
 * sleep. Without a window, one wake costs three refetches and two rejoins.
 */
const FOREGROUND_COALESCE_MS = 1_000

/**
 * One postgres_changes channel, kept alive.
 *
 * Every realtime hook in the apps used to be the same twenty lines: open a
 * channel, bind, subscribe, remove on unmount. None of them looked at the
 * subscribe status, so a channel that never joined — or one whose socket died
 * hours ago — was indistinguishable from a quiet gym. Members watched a
 * cancelled plan read "Active" until they force-reloaded.
 *
 * So this does three things the hand-rolled version did not:
 *
 *  - **Reports failure.** `CHANNEL_ERROR`/`TIMED_OUT`/`CLOSED` set the status to
 *    'degraded', warn to the console (Sentry picks those up as breadcrumbs) and
 *    reach `onStatus`, instead of vanishing.
 *  - **Rejoins.** Exponential backoff with jitter, and immediately when the tab
 *    is foregrounded or the network returns.
 *  - **Never relies on the socket for correctness.** `onResync` runs on every
 *    re-subscribe (catching up on what was missed while down), on foreground,
 *    and on a slow poll while degraded. A permanently dead socket costs
 *    freshness, not correctness.
 *
 * Callbacks live in refs, so inline closures and array literals do not tear the
 * channel down and rebuild it on every render.
 */
export function useRealtimeSync(options: RealtimeSyncOptions): RealtimeSyncStatus {
  const { channel, bindings, degradedPollMs = DEFAULT_DEGRADED_POLL_MS } = options
  const supabase = useAppSupabase()
  const [status, setStatus] = useState<RealtimeSyncStatus>('idle')
  // Mirrored in a ref because the timers and listeners below need to read the
  // current status without re-creating themselves every time it changes.
  const statusRef = useRef<RealtimeSyncStatus>('idle')
  const track = useCallback((next: RealtimeSyncStatus) => {
    statusRef.current = next
    setStatus(next)
  }, [])

  const bindingsRef = useRef(bindings)
  const onChangeRef = useRef(options.onChange)
  const onResyncRef = useRef(options.onResync)
  const onStatusRef = useRef(options.onStatus)
  useEffect(() => {
    bindingsRef.current = bindings
    onChangeRef.current = options.onChange
    onResyncRef.current = options.onResync
    onStatusRef.current = options.onStatus
  })

  const resync = useCallback(() => {
    const onResync = onResyncRef.current
    if (onResync) onResync()
    else
      onChangeRef.current({} as unknown as RealtimePostgresChangesPayload<Record<string, unknown>>)
  }, [])

  // The bindings are a value, not an identity: a caller writing them inline
  // produces a new array every render, and rebuilding the channel each time
  // would mean it never stays joined long enough to deliver anything.
  const bindingsKey = JSON.stringify(bindings)

  useEffect(() => {
    if (!channel) {
      track('idle')
      return
    }

    let disposed = false
    let current: RealtimeChannel | null = null
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    let attempt = 0
    let joinedOnce = false

    const clearRetry = () => {
      if (retryTimer) clearTimeout(retryTimer)
      retryTimer = undefined
    }

    const join = () => {
      if (disposed) return
      clearRetry()
      track('connecting')

      let ch = supabase.channel(channel)
      for (const b of bindingsRef.current) {
        ch = ch.on(
          // The overloads are keyed on the literal 'postgres_changes'; the
          // filter object is built at runtime, so it cannot be narrowed here.
          'postgres_changes' as never,
          {
            event: b.event ?? '*',
            schema: b.schema ?? 'public',
            table: b.table,
            ...(b.filter ? { filter: b.filter } : {}),
          } as never,
          ((payload: RealtimePostgresChangesPayload<Record<string, unknown>>) =>
            onChangeRef.current(payload)) as never,
        )
      }
      // Captured so the callback below can tell whether it still speaks for the
      // live channel: `rejoin()` unsubscribes the one it replaces, which fires
      // CLOSED, and an error arriving late would schedule a second retry timer
      // racing the new channel — and report a failure that already recovered.
      const joined = ch
      current = joined

      joined.subscribe((subscribeStatus, err) => {
        if (disposed || joined !== current) return
        onStatusRef.current?.(subscribeStatus, err)

        if (subscribeStatus === 'SUBSCRIBED') {
          attempt = 0
          track('live')
          // Only on a *re*-join. The data hooks already loaded on mount, and
          // resyncing on the first join would double every request on boot.
          if (joinedOnce) resync()
          joinedOnce = true
          return
        }

        // CHANNEL_ERROR / TIMED_OUT / CLOSED.
        console.warn('[realtime]', channel, subscribeStatus, err?.message ?? '')
        track('degraded')
        if (retryTimer) return
        const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt++) * (0.5 + Math.random())
        retryTimer = setTimeout(rejoin, delay)
      })
    }

    const rejoin = () => {
      if (disposed) return
      clearRetry()
      const stale = current
      current = null
      if (stale) void supabase.removeChannel(stale)
      join()
    }

    let lastForegroundAt = 0
    // A window regaining focus without having been hidden is a desk computer
    // clicking between the browser and another app. The page stayed on screen
    // and a live socket kept delivering, so re-reading everything would only
    // spend database calls. Hidden pages, phones waking and the network coming
    // back still resync, and so does any focus while the socket is not live.
    let hiddenSinceForeground = false
    const onForeground = (event: Event) => {
      if (disposed) return
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        hiddenSinceForeground = true
        return
      }
      if (event.type === 'focus' && !hiddenSinceForeground && statusRef.current === 'live') return
      hiddenSinceForeground = false
      const now = Date.now()
      if (now - lastForegroundAt < FOREGROUND_COALESCE_MS) return
      lastForegroundAt = now
      // Whatever the socket is doing, read the truth from the API.
      resync()
      if (statusRef.current !== 'live') rejoin()
    }

    join()

    document.addEventListener('visibilitychange', onForeground)
    window.addEventListener('focus', onForeground)
    window.addEventListener('online', onForeground)

    const poll =
      degradedPollMs > 0
        ? setInterval(() => {
            if (statusRef.current !== 'live') resync()
          }, degradedPollMs)
        : undefined

    return () => {
      disposed = true
      clearRetry()
      if (poll) clearInterval(poll)
      document.removeEventListener('visibilitychange', onForeground)
      window.removeEventListener('focus', onForeground)
      window.removeEventListener('online', onForeground)
      if (current) void supabase.removeChannel(current)
    }
    // bindingsKey stands in for `bindings` by value; the callbacks are refs.
  }, [channel, bindingsKey, supabase, degradedPollMs, resync, track])

  return status
}
